import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { Column, InfoIcon, TableList, Typography } from 'seagull';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
import AccountingCustomerDropdown from '~/integrations/Accounting/AccountingCustomerDropdown/AccountingCustomerDropdown';
import { OptionType } from '~/integrations/Accounting/AccountingCustomerDropdown/AccountingCustomerDropdown.utils';
import { FailureReasons } from '~/integrations/Accounting/MapContactsModal/MapContactsModal.utils';
import { selectors } from '~/store/state-selectors';
import { useStore, useStoreActions } from '~/store/store-context';
import { getAccountingDetails } from '../accounting.utils';
import { MapStatusView } from '../MapStatusView';
import styles from './MapContactsRow.module.scss';
import { useContactMapStatus } from './useContactMapStatus';
const ANCHOR_CONTACT_TEST_ID = 'anchor-contact';
export default function MapContactsRow(props) {
    const { anchorContact, accountingCustomers, integratedAccountingCustomer, changeContactLink, createNewCustomer, testId, onInputFocus, onInputBlur, accounting } = props;
    const { mapStatus, startMapping, finishMappingSuccess, finishMappingFailure } = useContactMapStatus(anchorContact, accounting, accountingCustomers);
    const [failureReason, setFailureReason] = useState(null);
    const vendorId = useStore(selectors.user.businessId);
    const userId = useStore(selectors.user.id);
    const { logBI } = useStoreActions();
    const { FE_CRM } = useFeatureFlags();
    const { testIdPrefix, shortName, fullName, biCodes } = getAccountingDetails(accounting);
    const contactFullName = [
        anchorContact.firstName(),
        anchorContact.lastName()
    ].filter(Boolean).join(' ');
    const contactNameWithEmail = [
        contactFullName,
        anchorContact.email() ? "(".concat(anchorContact.email(), ")") : ''
    ].filter(Boolean).join(' ');
    const changeMap = async (accountingCustomerId)=>{
        startMapping();
        setFailureReason(null);
        try {
            await changeContactLink(anchorContact, accountingCustomerId);
            finishMappingSuccess(accountingCustomerId ? 'mapped' : 'notMapped');
        } catch (err) {
            finishMappingFailure();
            const isDuplicateError = new ErrorResponse(err).status === 409;
            setFailureReason(isDuplicateError ? FailureReasons.DuplicateMapping : FailureReasons.General);
        }
    };
    const createNew = async ()=>{
        setFailureReason(null);
        startMapping();
        try {
            await createNewCustomer(anchorContact);
            finishMappingSuccess('mapped');
        } catch (e) {
            finishMappingFailure();
            const isDuplicateError = new ErrorResponse(e).hasServerError(ServerError.DuplicateNameExists);
            setFailureReason(isDuplicateError ? FailureReasons.DuplicateNew : FailureReasons.General);
        }
    };
    const onAccountingCustomerChange = async (option)=>{
        if (!option) {
            logBI(biCodes.unlinkContact, {
                vendorId,
                userId
            });
            changeMap();
            return;
        }
        switch(option.type){
            case OptionType.Existing:
                {
                    logBI(biCodes.linkContact, {
                        vendorId,
                        userId
                    });
                    changeMap(option.customer.id());
                    break;
                }
            case OptionType.CreateNew:
                {
                    logBI(biCodes.pushNewContact, {
                        vendorId,
                        userId
                    });
                    createNew();
                    break;
                }
        }
    };
    return /*#__PURE__*/ _jsx(TableList.Row, {
        testId: testId,
        items: [
            {
                value: FE_CRM ? /*#__PURE__*/ _jsxs(Column, {
                    gap: 4,
                    testId: ANCHOR_CONTACT_TEST_ID,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            ellipsis: true,
                            children: contactNameWithEmail
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            ellipsis: true,
                            skin: "secondary",
                            children: anchorContact.companyName()
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs("div", {
                    className: styles.anchorContactOld,
                    "data-testid": "anchor-contact",
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            ellipsis: true,
                            children: "".concat(anchorContact.companyName())
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: styles.anchorContactSecondLine,
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph2",
                                        ellipsis: true,
                                        children: "".concat(anchorContact.firstName(), " ").concat(anchorContact.lastName())
                                    }),
                                    anchorContact.email() ? /*#__PURE__*/ _jsx(Typography, {
                                        ellipsis: true,
                                        variant: "paragraph2",
                                        children: "".concat(anchorContact.email())
                                    }) : null
                                ]
                            })
                        })
                    ]
                })
            },
            {
                value: /*#__PURE__*/ _jsx("div", {
                    "data-testid": "".concat(testIdPrefix, "-contact"),
                    className: styles.autocompleteWrapper,
                    children: /*#__PURE__*/ _jsx(AccountingCustomerDropdown, {
                        accounting: accounting,
                        accountingCustomers: accountingCustomers,
                        value: integratedAccountingCustomer ? {
                            type: OptionType.Existing,
                            customer: integratedAccountingCustomer
                        } : null,
                        onChange: onAccountingCustomerChange,
                        onInputFocus: onInputFocus,
                        onInputBlur: onInputBlur
                    })
                })
            },
            {
                value: /*#__PURE__*/ _jsxs("div", {
                    className: styles.status,
                    children: [
                        /*#__PURE__*/ _jsx(MapStatusView, {
                            mapStatus: mapStatus
                        }),
                        failureReason === FailureReasons.DuplicateMapping && /*#__PURE__*/ _jsx(InfoIcon, {
                            testId: "duplicate-contact-map-info",
                            info: /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    "This ",
                                    shortName,
                                    " customer is already synced",
                                    /*#__PURE__*/ _jsx("br", {}),
                                    "with another Anchor contact"
                                ]
                            })
                        }),
                        failureReason === FailureReasons.DuplicateNew && /*#__PURE__*/ _jsx(InfoIcon, {
                            testId: "duplicate-contact-new-info",
                            info: /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    "Could not create a new client in ",
                                    fullName,
                                    ". A client with these details already exists"
                                ]
                            })
                        })
                    ]
                })
            }
        ]
    });
}
